import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type ComponentType,
  type FC,
  type PropsWithChildren,
} from 'react';

import type { CmsEditField, CmsRendering } from '@eneco/cms-adapter';
import logger from '@eneco/common/log';

import {
  ANCHOR,
  DATA_SANITY,
  DRAG_DISABLE,
  DRAG_GROUP,
  MARKER_KIND,
  MARKER_MATCH,
  locateEditFields,
  renderedElementOf,
} from './edit-dom';
import type { RegisteredMarker } from './edit-dom';
import { ComponentErrorBoundary } from './error-boundary';
import { useCms } from './provider';
import type { EditAttribute } from './provider';
import { RenderingContext } from './rendering-context';
import type { WrapComponent } from './types';

interface PlaceholderProps {
  name: string;
}

/**
 * Renders every child rendering registered under `name` in the current
 * parent rendering's `placeholders`. At the page root, falls back to the
 * page root's placeholders so the top-level layout works without a parent.
 *
 * Each rendered component is wrapped in the following order (outer → inner):
 *
 * ```
 * RenderingContext.Provider   ← exposes this rendering to useContent()/usePlaceholder()
 *   └─ ComponentErrorBoundary ← catches errors, logs to App Insights, isolates failures
 *        └─ WrapComponent     ← optional enhancer from CmsProvider (e.g. CmsEnhancer:
 *             └─ Component         GTM tracking + NBA feedback)
 * ```
 *
 * The enhancer sits *inside* the error boundary so a crash in tracking/NBA is
 * caught too and never takes down the whole page. See `docs/cms-component-rendering.md`.
 */
export const Placeholder: FC<PlaceholderProps> = ({ name }) => {
  const { page } = useCms();
  const parent = useContext(RenderingContext) ?? page.root;
  const children = parent.placeholders?.[name] ?? [];

  // No marker for the slot itself. Sanity's drag-and-drop is driven by the *items* — "you must
  // apply data attributes to the array items, and optionally the array parent if you want to
  // enable click-to-edit for it" — and a slot has no single rendered element of its own here: its
  // children are placed by whatever component owns the layout. The only node we could mark is a
  // wrapper, and a wrapper cannot be marked usefully (see `EditTarget`). `placeholderPaths` stays
  // on the contract; it is the right path, waiting for a real element to sit on.
  return (
    <>
      {children.map(child => (
        <Rendering key={child.uid} rendering={child} />
      ))}
    </>
  );
};

/**
 * How an `EditMarker` tells the block above it that it has mounted.
 *
 * Context rather than a DOM search, for two reasons. It crosses portals: Sparky's `Dialog` renders
 * its content on `document.body`, so `MegaMenuV2`'s mobile links are nowhere inside the block that
 * owns them and no lookup from the block's own element could find them. And it fires when the
 * marker mounts, so content that appears later — an opened dialog, a revealed panel, anything driven
 * by a descendant's own state — is picked up without the block having to render again.
 */
interface MarkerRegistry {
  register: (marker: RegisteredMarker) => () => void;
}
const MarkerRegistryContext = createContext<MarkerRegistry | undefined>(undefined);

interface EditTargetState {
  attribute: string;
  fields?: readonly CmsEditField[];
  editAttribute?: EditAttribute;
  dragGroup?: string;
}

type Attributes = Readonly<Record<string, string>>;

const sameAttributes = (a: Attributes, b: Attributes): boolean => {
  const names = Object.keys(a);
  return names.length === Object.keys(b).length && names.every(name => a[name] === b[name]);
};

const addBlockAttribute = (
  desired: Map<Element, Attributes>,
  root: HTMLElement,
  attribute: string,
  dragGroup: string | undefined,
): void => {
  const block = renderedElementOf(root);
  if (!block) return;

  desired.set(block, {
    [DATA_SANITY]: attribute,
    // A block is either reorderable among the siblings of its slot or not at all. Left unsaid, the
    // overlay would put it in one group with every other block of the same array — see
    // `CmsRendering.dragGroup`. "true" rather than "": the overlay tests the value's truthiness.
    ...(dragGroup === undefined ? { [DRAG_DISABLE]: 'true' } : { [DRAG_GROUP]: dragGroup }),
  });
};

const addFieldAttributes = (
  desired: Map<Element, Attributes>,
  root: HTMLElement,
  fields: readonly CmsEditField[] | undefined,
  markers: ReadonlySet<RegisteredMarker>,
  editAttribute: EditAttribute | undefined,
): void => {
  if (!editAttribute) return;

  for (const mark of locateEditFields(root, fields, markers)) {
    const value = editAttribute(mark.path, mark.document);
    if (value) desired.set(mark.element, { [DATA_SANITY]: value });
  }
};

const desiredAttributesFor = (
  root: HTMLElement,
  { attribute, fields, editAttribute, dragGroup }: EditTargetState,
  markers: ReadonlySet<RegisteredMarker>,
): Map<Element, Attributes> => {
  const desired = new Map<Element, Attributes>();

  addBlockAttribute(desired, root, attribute, dragGroup);
  // Set after the block, so a field on the very same element (an `Image` block whose only
  // element is the `<img>`) wins: it is the more specific answer. A field path is not an array
  // item, so the element is not draggable as that field and carries no drag attributes.
  addFieldAttributes(desired, root, fields, markers, editAttribute);

  return desired;
};

/**
 * Takes back whatever this target wrote and no longer wants, attribute by attribute: a block that
 * stops being reorderable must lose its group, not keep it beside a new `disable`.
 */
const clearStaleAttributes = (applied: Map<Element, Attributes>, desired: ReadonlyMap<Element, Attributes>): void => {
  for (const [element, previous] of applied) {
    const next = desired.get(element);
    if (next && sameAttributes(previous, next)) continue;
    for (const [name, value] of Object.entries(previous)) {
      if (next?.[name] === undefined && element.getAttribute(name) === value) element.removeAttribute(name);
    }
    if (!next) applied.delete(element);
  }
};

const isClaimedByChildTarget = (
  element: Element,
  applied: ReadonlyMap<Element, Attributes>,
  current: string | null,
): boolean => current !== null && applied.get(element)?.[DATA_SANITY] !== current;

const applyDesiredAttributes = (applied: Map<Element, Attributes>, desired: ReadonlyMap<Element, Attributes>): void => {
  for (const [element, next] of desired) {
    const current = element.getAttribute(DATA_SANITY);
    // Somebody else's — a child block claimed this element first (child effects run before
    // parent ones). Theirs is the more specific path, and stays, drag attributes included.
    if (isClaimedByChildTarget(element, applied, current)) continue;
    for (const [name, value] of Object.entries(next)) {
      if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    }
    applied.set(element, next);
  }
};

/**
 * Puts `data-sanity` on the elements the component already rendered.
 *
 * **Not on a wrapper.** Visual Editing measures the element carrying the attribute —
 * `getBoundingClientRect()` on that node, not on its children — and a `display: contents` box
 * generates no box at all, so it measures 0×0. Every overlay was therefore unclickable and
 * drag-and-drop inert, while text editing kept working because stega rides in the text node and
 * never consults this attribute. Measured on acceptance: 59 markers, all `0x0`.
 *
 * Sanity's docs describe both routes — the attribute spread onto your own element, or
 * `setAttribute('data-sanity', …)`. Spreading is impossible here: this lane renders 70-odd
 * components through a registry, and the design-system components they render (`Image`, the
 * illustrations) accept no pass-through props. So the wrapper stays purely as an anchor to find the
 * real elements from, and the attributes are set on them after mount.
 *
 * Two levels of target:
 *  - the block itself, on its outermost real element — the answer for a click on the block's
 *    background, and for anything with no field of its own
 *  - each of its `fields` that stega cannot express (images, illustrations), on the element that
 *    shows it, so a click lands on that field's control instead of somewhere near it
 *
 * **Kept current three ways**, because the elements being marked belong to components this one does
 * not control and change without it rendering:
 *  - after each of its own commits, for changed props
 *  - when an `EditMarker` below it mounts or unmounts, however far away it was rendered
 *  - when its DOM changes underneath it — a child revealing an image from its own state, or swapping
 *    its root element — via a `MutationObserver` on `childList`, `src` and `srcset`
 *
 * Reconciling is idempotent and only writes an attribute whose value changed, and `data-sanity`
 * itself is deliberately not observed, so the overlay's own observer sees nothing when nothing did
 * and this cannot trigger itself.
 *
 * The wrapper keeps `display: contents` because this lane's output must stay identical to
 * Sitecore's; a real box would become the flex or grid item in place of the child it wraps.
 *
 * Only ever rendered in draft mode. A published page emits none of this.
 */
const EditTarget: FC<
  PropsWithChildren<{
    attribute: string;
    fields?: readonly CmsEditField[];
    editAttribute?: EditAttribute;
    dragGroup?: string;
  }>
> = ({ attribute, fields, editAttribute, dragGroup, children }) => {
  const anchor = useRef<HTMLDivElement>(null);
  /** What this target has written, so a re-run can tell its own attributes from another block's. */
  const applied = useRef(new Map<Element, Attributes>());
  const markers = useRef(new Set<RegisteredMarker>());
  /** The props of the latest commit, for the callbacks below that outlive a single render. */
  const latest = useRef<EditTargetState>({ attribute, fields, editAttribute, dragGroup });

  const reconcile = useCallback(() => {
    const root = anchor.current;
    if (!root) return;
    const desired = desiredAttributesFor(root, latest.current, markers.current);

    clearStaleAttributes(applied.current, desired);
    applyDesiredAttributes(applied.current, desired);
  }, []);

  const registry = useMemo<MarkerRegistry>(
    () => ({
      register: marker => {
        markers.current.add(marker);
        reconcile();
        return () => {
          markers.current.delete(marker);
          reconcile();
        };
      },
    }),
    [reconcile],
  );

  // No dependency list, on purpose: props of this block can change on any commit of it.
  useLayoutEffect(() => {
    latest.current = { attribute, fields, editAttribute, dragGroup };
    reconcile();
  });

  useLayoutEffect(() => {
    const root = anchor.current;
    if (!root) return undefined;

    const observer = new MutationObserver(() => reconcile());
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });

    const written = applied.current;
    return () => {
      observer.disconnect();
      for (const [element, attributes] of written) {
        for (const [name, value] of Object.entries(attributes)) {
          if (element.getAttribute(name) === value) element.removeAttribute(name);
        }
      }
      written.clear();
    };
  }, [reconcile]);

  return (
    <MarkerRegistryContext.Provider value={registry}>
      {/* eslint-disable-next-line dxp-rules/no-inline-css -- `display: contents` is the entire point of this element: it anchors the lookup above and must contribute nothing to layout. A Sparky component would render a real box and change how every flex/grid parent lays out its children, which is exactly the regression this lane cannot have. */}
      <div ref={anchor} {...{ [ANCHOR]: '' }} style={{ display: 'contents' }}>
        {children}
      </div>
    </MarkerRegistryContext.Provider>
  );
};

/**
 * Wraps something a Visual Editing overlay cannot find on its own, so its field can be marked.
 *
 * An illustration is an SVG drawn from a lookup key: nothing in the DOM says which field it came
 * from, and the design-system component takes no `data-*` prop to say so. In draft mode this puts a
 * `display: contents` marker around it and registers that marker with the enclosing block, which
 * resolves it to the real element and marks it. Registration goes through React context, so it holds
 * wherever the marker is rendered — including through a portal. Outside draft mode this renders
 * nothing of its own, so a published page is unchanged.
 */
export const EditMarker: FC<PropsWithChildren<{ kind: CmsEditField['kind']; match: string }>> = ({
  kind,
  match,
  children,
}) => {
  const { editAttribute } = useCms();
  const registry = useContext(MarkerRegistryContext);
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!registry || !element) return undefined;
    return registry.register({ element, kind, match });
  }, [registry, kind, match]);

  if (!editAttribute) return children;

  return (
    // eslint-disable-next-line dxp-rules/no-inline-css -- must contribute nothing to layout; see `EditTarget`
    <span ref={ref} {...{ [MARKER_KIND]: kind, [MARKER_MATCH]: match }} style={{ display: 'contents' }}>
      {children}
    </span>
  );
};

/**
 * Renders one rendering, with the same wrapping `Placeholder` gives its children.
 *
 * Exists so a parent can place its children individually rather than as one opaque group — a
 * carousel needs each slide in its own `Carousel.Item`, and needs to know how many there are, which
 * `<Placeholder>` cannot express. Pair with `usePlaceholder(name)` to get the list. The legacy lane
 * had `usePlaceholderComponentsForRendering` for the same reason.
 *
 * Prefer `<Placeholder>` wherever the group is what you want; reach for this only when the parent
 * genuinely needs per-child control.
 */
export const Rendering: FC<{ rendering: CmsRendering }> = ({ rendering }) => {
  const { components, wrapComponent, editAttribute } = useCms();
  const node = renderComponent(rendering, components, wrapComponent);
  const attribute = editAttribute && rendering.fieldPath ? editAttribute(rendering.fieldPath) : undefined;

  return (
    <RenderingContext.Provider value={rendering}>
      {attribute ? (
        <EditTarget
          attribute={attribute}
          fields={rendering.editFields}
          editAttribute={editAttribute}
          dragGroup={rendering.dragGroup}>
          {node}
        </EditTarget>
      ) : (
        node
      )}
    </RenderingContext.Provider>
  );
};

const renderComponent = (
  rendering: CmsRendering,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components: Record<string, ComponentType<any>>,
  WrapOuter?: WrapComponent,
) => {
  const Component = components[rendering.componentName];
  if (!Component) {
    // Logged in all environments so missing components surface in App Insights on
    // test/acceptance during the migration, not just in a developer's console.
    logger.warn('cMsPh1', 'No component registered for CMS componentName', {
      componentName: rendering.componentName,
      uid: rendering.uid,
    });
    if (process.env.NODE_ENV !== 'production') {
      return <div data-cms-missing={rendering.componentName}>[missing component: {rendering.componentName}]</div>;
    }
    return null;
  }

  // Transitional: pass rendering data as props so Sitecore components work when rendered via this
  // placeholder instead of Sitecore's JSS Placeholder. A Sitecore rendering carries the fields as
  // Sitecore sent them (`rawFields`), which is what Sitecore's own Placeholder hands a component, so
  // a component reading raw JSS fields renders as it does there. Any other rendering passes its
  // content; the adapter's unwrapFields is idempotent, so a bridge re-mapping it gets it unchanged.
  // `placeholders` is intentionally NOT forwarded: it contains CmsRendering[] children,
  // but adapter.toRendering() expects JssRendering[] (with `fields`, not `content`).
  // Forwarding would cause toRendering() to read undefined fields and wipe nested content.
  // Bridges with nested placeholder slots of their own must be rendered by Sitecore's JSS Placeholder.
  // Remove this whole block once all bridges are gone.
  const node = createElement(Component, {
    fields: rendering.rawFields ?? rendering.content,
    params: rendering.params,
    uid: rendering.uid,
    componentName: rendering.componentName,
  });

  const enhanced = WrapOuter
    ? createElement(WrapOuter, { componentName: rendering.componentName, params: rendering.params }, node)
    : node;

  return <ComponentErrorBoundary componentName={rendering.componentName}>{enhanced}</ComponentErrorBoundary>;
};
